import cloneDeep from 'lodash/cloneDeep'
import noop from 'lodash/noop'
import isEqual from 'lodash/isEqual'
import intersectionWith from 'lodash/intersectionWith'
import uniq from 'lodash/uniq'
import isPlainObject from 'lodash/isPlainObject'
import isArray from 'lodash/isArray'

import { hasValidationError } from 'src/services'
import ObjectObserver from './ObjectObserver'
import ValueObserver from './ValueObserver'

export default class FormObserver {
  // form config
  onSubmit = noop
  validate = noop

  // initialValues observer : an observer for storing the current initialValues of the form
  initialValues = new ObjectObserver()

  // values observer : an observer for storing the current values of the form
  values = new ObjectObserver()

  // errors observer : an observer for storing the current errors of the form
  errors = new ObjectObserver()

  // dirties observer : an observer for storing the dirty paths of the form (values !== initialValues)
  dirties = new ObjectObserver()

  // submitting observer : an observer for storing the submitting state of the form
  isSubmitting = new ValueObserver(false)

  // disabled observer : an observer for storing the disabled state of the form
  isDisabled = new ValueObserver(false)

  /**
   * The form constructor : initialize subscriptions
   */
  constructor({ initialValues }) {
    // reset values when initialValues changes
    this.initialValues.subscribe(() => this.reset())

    // set initial values
    this.initialValues.set(initialValues)

    // update dirties and errors on each value change
    this.values.subscribe((value, { path }) => {
      // set dirty paths
      this.dirties.set(getDirtyPaths(this.values.get(path), this.initialValues.get(path)), path)
      // when specific path value is updated, remove error at that path
      this.errors.unset(path)
    })
  }

  /**
   * The reset handler, used to reset all or a specific path of the form
   */
  reset = path => this.values.set(cloneDeep(this.initialValues.get(path)), path)

  /**
   * The change handler, used to update all or a specific path of the form
   */
  change = (path, value) => this.values.set(value, path)

  /**
   * The submit handler, used to validate and submit the form
   */
  submit = () =>
    Promise.resolve()
      // set submitting to true
      .then(() => this.isSubmitting.set(true))
      // validate form and store errors if any
      .then(() => typeof this.validate === 'function' && this.errors.set(this.validate(this.values.get())))
      // throw errors if any
      .then(() => {
        if (hasValidationError(this.errors.get())) throw this.errors.get()
      })
      // submit form if no errors
      .then(() => typeof this.onSubmit === 'function' && this.onSubmit(this.values.get()))
      // set submitting back to false when resolved or rejected
      .finally(() => this.isSubmitting.set(false))
}

/**
 * Check if two values are deeply equal
 * Same as lodash isEqual except that arrays orders has no importance
 */
function areValuesEqual(a, b) {
  if (isArray(a) && isArray(b)) {
    return a.length === b.length && intersectionWith(a, b, (c, d) => areValuesEqual(c, d)).length === a.length
  } else {
    return isEqual(a, b)
  }
}

/**
 * Deeply explore two objects and return a combination where each final key has a boolean value about if the original values at that key are different or not
 */
function getDirtyPaths(values, initialValues) {
  const valuesObject = isPlainObject(values) ? values : {}
  const initialValuesObject = isPlainObject(initialValues) ? initialValues : {}
  const keys = uniq([...Object.keys(valuesObject), ...Object.keys(initialValuesObject)])
  if (keys.length) {
    return keys.reduce(
      (result, key) => ({
        ...result,
        [key]: getDirtyPaths(valuesObject[key], initialValuesObject[key]),
      }),
      {}
    )
  } else {
    return !areValuesEqual(values, initialValues)
  }
}
